import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import type { FunnelFacts, TenantAnalytics } from '../types';
import type { AccountSummary } from './account-types';
import { journeyMilestones } from '../analytics/journey-words';
import './account.css';

/** What the account itself knows, while the journey is loading or has no funnel facts: never an empty row. */
function factsOf(a: AccountSummary): FunnelFacts {
  return {
    signedUpAt: a.crm.signedUpAt,
    source: a.crm.source ?? 'direct',
    campaign: a.crm.campaign ?? '',
    firstJobAt: null,
    firstPagesAt: null,
    firstPagesSource: null,
    firstExportAt: a.crm.firstExportAt,
    planChosenAt: null,
  };
}

/**
 * The company's road so far in one line (rounds 2 and 3, marketing N7 and Must 2): signed up from
 * where, first job, first pages, first export (activated), plan chosen, each with how long after the
 * step before. A step not reached is muted, and the row always renders: from the journey's funnel
 * facts when they are there, from the account's own facts when not.
 */
export function MilestoneRow(x: {
  analytics: TenantAnalytics | undefined;
  account: AccountSummary;
}): ReactNode {
  const f = useFormat();
  const facts = x.analytics?.funnel ?? factsOf(x.account);
  const steps = journeyMilestones(facts, x.analytics?.recent[0]?.at ?? null);
  return (
    <ol className="ad-milestones" aria-label="The road so far">
      {steps.map((m) => (
        <li key={m.key} className={m.state === 'done' ? 'is-done' : 'is-pending'}>
          {m.text}
          {m.at ? ` (${m.elapsed ?? f.date(m.at)})` : ''}
        </li>
      ))}
    </ol>
  );
}
